import { useLocation, useSearchParams } from "react-router-dom";
import { Loader2, Video } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { secureRandom } from "../../../components/streaming/utils/secureRandom";
import RecordingPlayer from "../../../components/SecureVideoPlayer";
import VideoControlBar from "../../../components/streaming/VideoControlBar";
import {
  mobileFullscreenCss,
  useMobileFullscreen,
} from "../../../components/streaming/utils/useMobileFullscreen";
import {
  useAutoHideControls,
  useVideoState,
} from "../../../components/streaming/utils/useVideoState";
import { useAuth } from "../../../context/AuthProvider";
import { getVideoV2 } from "../../../api/archivalReqestApis";

const VIDEO_PLAYER_CSS = `
  video {
    width: 100%;
    height: 100%;
    display: block;
    background: black;
  }
  .video-container {
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
  /* Phones in portrait: 16:9 box at the top instead of a full-height
     box with the video lost in the middle of black space */
  @media (max-width: 768px) {
    .video-container {
      height: auto;
      aspect-ratio: 16 / 9;
      max-height: 100%;
    }
  }
  ${mobileFullscreenCss(".video-container")}
`;

interface StreamViewRecordingProps {
  videoUrl?: string;
  user?: any;
  cnr?: string;
  hearingDate?: string;
}

const NoVideo = () => (
  <div className="w-full h-full flex items-center justify-center bg-black">
    <Video size={40} className="text-red-600" />
    <p className="text-white ml-3 text-sm">No video available</p>
  </div>
);

/**
 * Resolves the video to play. Route state only lives in one history entry,
 * so after a reload / new tab it can be missing — then the cnr +
 * hearingDate from the URL query are used to fetch a fresh video URL.
 */
const StreamViewRecording = (props: StreamViewRecordingProps = {}) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user: authUser } = useAuth();
  const routeState = location.state || {};
  const stateUrl: string | undefined = props.videoUrl || routeState.videoUrl;
  const user = props.user || routeState.user || authUser;
  const cnr: string | undefined =
    props.cnr || routeState.cnr || searchParams.get("cnr") || undefined;
  const hearingDate: string | undefined =
    props.hearingDate ||
    routeState.hearingDate ||
    searchParams.get("hearingDate") ||
    undefined;

  const [fetchedUrl, setFetchedUrl] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (stateUrl || !cnr || !hearingDate) return;
    let cancelled = false;
    setFetching(true);
    getVideoV2({ cnr, hearingDate })
      .then((res) => {
        if (!cancelled && res.data?.status) {
          setFetchedUrl(res.data.data?.tokenUrl || null);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [stateUrl, cnr, hearingDate]);

  const videoUrl = stateUrl || fetchedUrl;

  if (!videoUrl) {
    return fetching ? (
      <div className="w-full h-full flex items-center justify-center bg-black">
        <Loader2 size={32} className="animate-spin text-white" />
      </div>
    ) : (
      <NoVideo />
    );
  }

  return (
    <RecordingView
      // remount on a new video so the player hooks bind to the new element
      key={videoUrl}
      videoUrl={videoUrl}
      user={user}
      cnr={cnr}
      hearingDate={hearingDate}
    />
  );
};

const RecordingView = ({
  videoUrl,
  user,
  cnr,
  hearingDate,
}: StreamViewRecordingProps & { videoUrl: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [pulse, setPulse] = useState(0.4);
  const [randPos, setRandPos] = useState({ top: 10, left: 10 });
  const [timestamp, setTimestamp] = useState(Date.now());

  const { fullscreenActive, pseudoFullscreen, rotated, toggleFullscreen } =
    useMobileFullscreen(containerRef, videoRef);
  const { controlsVisible, showControls, canHover } = useAutoHideControls();
  const { paused, muted, currentTime, duration, toggleMute, seek } =
    useVideoState(videoRef, { trackTime: true });

  // stable so the memoized RecordingPlayer doesn't re-render every tick
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  }, []);

  // Watermark animation
  useEffect(() => {
    const pos = setInterval(() => {
      setRandPos({
        top: secureRandom() * 80 + 5,
        left: secureRandom() * 80 + 5,
      });
    }, 2500);

    const pulseI = setInterval(() => {
      setPulse((p) => (p === 0.4 ? 0.2 : 0.4));
    }, 1500);

    const timeI = setInterval(() => setTimestamp(Date.now()), 1000);

    return () => {
      clearInterval(pos);
      clearInterval(pulseI);
      clearInterval(timeI);
    };
  }, []);

  const watermarkText = user
    ? `${user.name} | ${user.email} | ${user.phone} | ${user.ip} | ${new Date(timestamp).toLocaleString()}`
    : "";

  return (
    <div className="relative w-full bg-black h-full content-center">
      <div
        ref={containerRef}
        // capture: a tap on the video still reveals the control bar
        onPointerDownCapture={showControls}
        onPointerMove={showControls}
        className={`video-container relative bg-black${
          pseudoFullscreen ? " pseudo-fullscreen" : ""
        }${rotated ? " rotated" : ""}${controlsVisible ? "" : " cursor-none"}`}
      >
        <RecordingPlayer
          ref={videoRef}
          src={videoUrl}
          cnr={cnr}
          hearingDate={hearingDate}
          nativeControls={false}
          fill
          onVideoClick={canHover ? togglePlay : undefined}
        />

        <style>{VIDEO_PLAYER_CSS}</style>

        <VideoControlBar
          visible={controlsVisible}
          paused={paused}
          muted={muted}
          onTogglePlay={togglePlay}
          onToggleMute={toggleMute}
          onToggleFullscreen={toggleFullscreen}
          fullscreenActive={fullscreenActive}
          currentTime={currentTime}
          duration={duration}
          onSeek={seek}
        />

        {/* Watermark */}
        {user && watermarkText && (
          <div
            className="absolute z-[9999] pointer-events-none hidden sm:block select-none"
            style={{
              top: `${randPos.top}%`,
              left: `${randPos.left}%`,
              opacity: pulse,
              color: "white",
              fontSize: "16px",
              whiteSpace: "normal",
              maxWidth: "90%",
              textShadow: "1px 1px 3px rgba(0,0,0,0.9)",
              transition: "opacity 0.5s ease",
            }}
          >
            {watermarkText}
          </div>
        )}
      </div>
    </div>
  );
};

export default StreamViewRecording;
